/* =====================================================================
 * DocxToolbar — DOCX 편집 툴바
 * 폰트/크기/볼드/이탤릭/밑줄/취소선/정렬/색상/undo/redo/다운로드
 * ===================================================================== */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Undo2,
  Redo2,
  Download,
  Type,
  Minus,
  Plus,
  ChevronDown,
} from 'lucide-react';
import { Tooltip } from '@auw/workspace-ui';
import { DropdownMenu } from '@auw/workspace-ui';
import type { DropdownMenuItem } from '@auw/workspace-ui';
import type { FormatState } from './useDocxEditor';

/* ------------------------------------------------------------------ */
/* 폰트 목록                                                            */
/* ------------------------------------------------------------------ */

const FONT_LIST = [
  // 한글 폰트
  '맑은 고딕',
  '나눔고딕',
  '나눔명조',
  '돋움',
  '굴림',
  '바탕',
  // 영문 폰트
  'Calibri',
  'Arial',
  'Times New Roman',
  'Georgia',
  'Verdana',
  'Helvetica',
  'Courier New',
  'Tahoma',
  'Trebuchet MS',
  'Segoe UI',
  'Roboto',
];

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const ToolbarContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.colors.bgPrimary};
  flex-shrink: 0;
  gap: 2px;
  flex-wrap: wrap;
`;

const ToolbarBtn = styled.button<{ $active?: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme, $active }) => $active ? theme.colors.accent : theme.colors.textMuted};
  background: ${({ $active, theme }) => $active ? theme.colors.bgHover : 'transparent'};
  transition: all ${({ theme }) => theme.transitions.fast};
  flex-shrink: 0;
  position: relative;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 20px;
  background: ${({ theme }) => theme.colors.border};
  margin: 0 4px;
  flex-shrink: 0;
`;

const FontSizeGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  flex-shrink: 0;
`;

const FontSizeInput = styled.input`
  width: 36px;
  height: 24px;
  text-align: center;
  font-size: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  outline: none;
  padding: 0;

  &:focus {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const ColorPickerWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const HiddenColorInput = styled.input`
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  overflow: hidden;
`;

const ColorIndicator = styled.div<{ $color?: string }>`
  width: 12px;
  height: 3px;
  background: ${({ $color }) => $color || '#000000'};
  position: absolute;
  bottom: 3px;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 1px;
`;

const FontSelectBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 5px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.75rem;
  cursor: pointer;
  flex-shrink: 0;
  min-width: 120px;
  max-width: 180px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    border-color: ${({ theme }) => theme.colors.accent};
  }

  svg {
    flex-shrink: 0;
    margin-left: auto;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const FontNameSpan = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  text-align: left;
`;

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

interface DocxToolbarProps {
  formatState: FormatState;
  canUndo: boolean;
  canRedo: boolean;
  onFormat: (format: Partial<FormatState>) => void;
  onUndo: () => void;
  onRedo: () => void;
  onDownload: () => void;
}

export const DocxToolbar: React.FC<DocxToolbarProps> = ({
  formatState,
  canUndo,
  canRedo,
  onFormat,
  onUndo,
  onRedo,
  onDownload,
}) => {
  const colorRef = useRef<HTMLInputElement>(null);
  const fontBtnRef = useRef<HTMLButtonElement>(null);
  const [fontSize, setFontSize] = useState<string>(
    formatState.fontSize ? String(formatState.fontSize) : '10'
  );
  const [fontDropdownOpen, setFontDropdownOpen] = useState(false);
  const [fontDropdownPos, setFontDropdownPos] = useState({ x: 0, y: 0 });

  // formatState.fontSize 변경 시 로컬 상태 동기화
  useEffect(() => {
    if (formatState.fontSize !== null) {
      setFontSize(String(formatState.fontSize));
    }
  }, [formatState.fontSize]);

  const toggleBold = useCallback(() => {
    onFormat({ bold: !formatState.bold });
  }, [formatState.bold, onFormat]);

  const toggleItalic = useCallback(() => {
    onFormat({ italic: !formatState.italic });
  }, [formatState.italic, onFormat]);

  const toggleUnderline = useCallback(() => {
    onFormat({ underline: !formatState.underline });
  }, [formatState.underline, onFormat]);

  const toggleStrike = useCallback(() => {
    onFormat({ strike: !formatState.strike });
  }, [formatState.strike, onFormat]);

  const handleAlign = useCallback((alignment: string) => {
    onFormat({ alignment: formatState.alignment === alignment ? null : alignment });
  }, [formatState.alignment, onFormat]);

  const handleFontSizeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFontSize(e.target.value);
  }, []);

  const handleFontSizeCommit = useCallback(() => {
    const size = parseInt(fontSize, 10);
    if (!isNaN(size) && size > 0 && size <= 400) {
      onFormat({ fontSize: size });
    }
  }, [fontSize, onFormat]);

  const handleFontSizeKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleFontSizeCommit();
    }
  }, [handleFontSizeCommit]);

  const incrementFontSize = useCallback(() => {
    const current = parseInt(fontSize, 10) || 11;
    const next = Math.min(current + 1, 400);
    setFontSize(String(next));
    onFormat({ fontSize: next });
  }, [fontSize, onFormat]);

  const decrementFontSize = useCallback(() => {
    const current = parseInt(fontSize, 10) || 11;
    const next = Math.max(current - 1, 1);
    setFontSize(String(next));
    onFormat({ fontSize: next });
  }, [fontSize, onFormat]);

  const handleColorClick = useCallback(() => {
    colorRef.current?.click();
  }, []);

  const handleColorChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onFormat({ color: e.target.value });
  }, [onFormat]);

  /* ── 폰트 드롭다운 ── */
  const handleFontDropdownOpen = useCallback(() => {
    if (fontBtnRef.current) {
      const rect = fontBtnRef.current.getBoundingClientRect();
      setFontDropdownPos({ x: rect.left, y: rect.bottom + 4 });
    }
    setFontDropdownOpen(true);
  }, []);

  const handleFontDropdownClose = useCallback(() => {
    setFontDropdownOpen(false);
  }, []);

  const fontMenuItems: DropdownMenuItem[] = FONT_LIST.map((font) => ({
    id: font,
    label: font,
    onClick: () => {
      onFormat({ fontFamily: font });
    },
  }));

  const displayFontName = formatState.fontFamily || 'Calibri';

  return (
    <ToolbarContainer>
      {/* Undo / Redo */}
      <Tooltip label="실행 취소 (Ctrl+Z)" position="bottom">
        <ToolbarBtn onClick={onUndo} disabled={!canUndo}>
          <Undo2 size={15} />
        </ToolbarBtn>
      </Tooltip>
      <Tooltip label="다시 실행 (Ctrl+Y)" position="bottom">
        <ToolbarBtn onClick={onRedo} disabled={!canRedo}>
          <Redo2 size={15} />
        </ToolbarBtn>
      </Tooltip>

      <Divider />

      {/* 폰트 선택 */}
      <Tooltip label="글꼴" position="bottom">
        <FontSelectBtn ref={fontBtnRef} onClick={handleFontDropdownOpen}>
          <FontNameSpan>{displayFontName}</FontNameSpan>
          <ChevronDown size={12} />
        </FontSelectBtn>
      </Tooltip>

      <DropdownMenu
        open={fontDropdownOpen}
        x={fontDropdownPos.x}
        y={fontDropdownPos.y}
        items={fontMenuItems}
        onClose={handleFontDropdownClose}
        direction="down"
        align="left"
      />

      <Divider />

      {/* 폰트 크기 */}
      <FontSizeGroup>
        <Tooltip label="글꼴 크기 줄이기" position="bottom">
          <ToolbarBtn onClick={decrementFontSize}>
            <Minus size={13} />
          </ToolbarBtn>
        </Tooltip>
        <FontSizeInput
          value={fontSize}
          onChange={handleFontSizeChange}
          onBlur={handleFontSizeCommit}
          onKeyDown={handleFontSizeKeyDown}
          title="글꼴 크기"
        />
        <Tooltip label="글꼴 크기 늘리기" position="bottom">
          <ToolbarBtn onClick={incrementFontSize}>
            <Plus size={13} />
          </ToolbarBtn>
        </Tooltip>
      </FontSizeGroup>

      <Divider />

      {/* 서식: 볼드 / 이탤릭 / 밑줄 / 취소선 */}
      <Tooltip label="굵게 (Ctrl+B)" position="bottom">
        <ToolbarBtn $active={formatState.bold} onClick={toggleBold}>
          <Bold size={15} />
        </ToolbarBtn>
      </Tooltip>
      <Tooltip label="기울임 (Ctrl+I)" position="bottom">
        <ToolbarBtn $active={formatState.italic} onClick={toggleItalic}>
          <Italic size={15} />
        </ToolbarBtn>
      </Tooltip>
      <Tooltip label="밑줄 (Ctrl+U)" position="bottom">
        <ToolbarBtn $active={formatState.underline} onClick={toggleUnderline}>
          <Underline size={15} />
        </ToolbarBtn>
      </Tooltip>
      <Tooltip label="취소선" position="bottom">
        <ToolbarBtn $active={formatState.strike} onClick={toggleStrike}>
          <Strikethrough size={15} />
        </ToolbarBtn>
      </Tooltip>

      <Divider />

      {/* 색상 */}
      <ColorPickerWrapper>
        <Tooltip label="글꼴 색상" position="bottom">
          <ToolbarBtn onClick={handleColorClick}>
            <Type size={15} />
            <ColorIndicator $color={formatState.color || '#000000'} />
          </ToolbarBtn>
        </Tooltip>
        <HiddenColorInput
          ref={colorRef}
          type="color"
          value={formatState.color || '#000000'}
          onChange={handleColorChange}
        />
      </ColorPickerWrapper>

      <Divider />

      {/* 정렬 */}
      <Tooltip label="왼쪽 정렬" position="bottom">
        <ToolbarBtn
          $active={formatState.alignment === 'left'}
          onClick={() => handleAlign('left')}
        >
          <AlignLeft size={15} />
        </ToolbarBtn>
      </Tooltip>
      <Tooltip label="가운데 정렬" position="bottom">
        <ToolbarBtn
          $active={formatState.alignment === 'center'}
          onClick={() => handleAlign('center')}
        >
          <AlignCenter size={15} />
        </ToolbarBtn>
      </Tooltip>
      <Tooltip label="오른쪽 정렬" position="bottom">
        <ToolbarBtn
          $active={formatState.alignment === 'right'}
          onClick={() => handleAlign('right')}
        >
          <AlignRight size={15} />
        </ToolbarBtn>
      </Tooltip>
      <Tooltip label="양쪽 정렬" position="bottom">
        <ToolbarBtn
          $active={formatState.alignment === 'both' || formatState.alignment === 'justify'}
          onClick={() => handleAlign('both')}
        >
          <AlignJustify size={15} />
        </ToolbarBtn>
      </Tooltip>

      <Divider />

      {/* 다운로드 */}
      <Tooltip label="다운로드" position="bottom" align="end">
        <ToolbarBtn onClick={onDownload}>
          <Download size={15} />
        </ToolbarBtn>
      </Tooltip>
    </ToolbarContainer>
  );
};
